import useStrings from "../../localization/useStrings"
import svg from "../../utils/svg"
import styles from "./BubbleFeatureList.module.scss"
import { useUltimateAlerts } from "../../context/ultimateAlertsContext"
import type { FeatureAlert } from "../../context/ultimateAlertsContext"

interface Feature {
  id: string
  IconComponent: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  title: string
  description: string
  alert?: FeatureAlert
}

interface BubbleFeatureListProps {
  useUltimateColor?: boolean
  iconColor?: string
}

const BubbleFeatureList = ({
  useUltimateColor = false,
  iconColor,
}: BubbleFeatureListProps) => {
  const strings = useStrings()
  const { actions } = useUltimateAlerts()

  const features: Feature[] = useUltimateColor
    ? [
        {
          id: "ultimate",
          IconComponent: svg.UltimateIcon,
          title: strings.bubbleExpiration.everyUltimateFeature,
          description: strings.bubbleExpiration.unlockUltimateTools,
        },
        {
          id: "cloud",
          IconComponent: svg.featureIcons.CloudVault,
          title: strings.bubbleExpiration.cloudVaultUltimate,
          description: strings.bubbleExpiration.secureBackups,
        },
        {
          id: "space",
          IconComponent: svg.featureIcons.DuplicateDetection,
          title: strings.bubbleExpiration.spaceSaver,
          description: strings.bubbleExpiration.freeUpSpace,
        },
        {
          id: "organization",
          IconComponent: svg.featureIcons.OrganizationPlus,
          title: strings.bubbleExpiration.organizationPlus,
          description: strings.bubbleExpiration.organizationPlusDesc,
          alert: "organization+",
        },
        {
          id: "ads",
          IconComponent: svg.featureIcons.Adfree,
          title: strings.bubbleExpiration.noAds,
          description: strings.bubbleExpiration.fullExperience,
        },
      ]
    : [
        {
          id: "premium",
          IconComponent: svg.PremiumIcon,
          title: strings.bubbleExpiration.everyPremiumFeature.replace(
            "\n",
            " ",
          ),
          description: strings.bubbleExpiration.unlockPrivacyTools,
        },
        {
          id: "cloud",
          IconComponent: svg.featureIcons.CloudVault,
          title: strings.bubbleExpiration.cloudVaultGB,
          description: strings.bubbleExpiration.secureBackups,
        },
        {
          id: "space",
          IconComponent: svg.featureIcons.DuplicateDetection,
          title: strings.bubbleExpiration.spaceSaver,
          description: strings.bubbleExpiration.freeUpSpace,
        },
        {
          id: "trash",
          IconComponent: svg.featureIcons.Trash,
          title: strings.bubbleExpiration.trashRecovery,
          description: strings.bubbleExpiration.recoverDeleted,
        },
        {
          id: "ads",
          IconComponent: svg.featureIcons.Adfree,
          title: strings.bubbleExpiration.noAds,
          description: strings.bubbleExpiration.fullExperience,
        },
      ]

  return (
    <div
      className={`${styles.featuresSection} ${useUltimateColor ? styles.ultimate : ""}`}
      style={
        iconColor
          ? ({ "--feature-icon-color": iconColor } as React.CSSProperties)
          : undefined
      }
    >
      {features.map((feature) => (
        <div key={feature.id} className={styles.feature}>
          <div className={styles.featureIcon}>
            <feature.IconComponent className={styles.featureSvg} />
          </div>
          <div className={styles.featureContent}>
            <label className={styles.featureTitle}>
              {feature.title}
              {feature.alert != null && (
                <button className={styles.infoContainer}>
                  <svg.Info
                    className={styles.info}
                    onClick={() => {
                      actions.setShowFeatureAlert(
                        feature.alert!,
                        "bubbleFeatureList",
                      )
                    }}
                  />
                </button>
              )}
            </label>
            <label className={styles.featureDescription}>
              {feature.description}
            </label>
          </div>
        </div>
      ))}
    </div>
  )
}

export default BubbleFeatureList
